import { useState } from "react";
import { MailCheck, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Quiet reminder for signed-in accounts that haven't verified yet. Purely
 *  informational — browsing, My Board and profile editing all stay open.
 *  Dismissal lasts for the session only, so it never nags across page loads. */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(
    () => sessionStorage.getItem("limya_verify_banner_hidden") === "1",
  );
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || user.role === "admin" || hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem("limya_verify_banner_hidden", "1");
    setHidden(true);
  };

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email. Please try again.");
    }
    setSending(false);
  };

  return (
    <div className="px-3 md:px-6 pt-3" data-testid="verify-email-banner">
      <div className="max-w-5xl mx-auto bg-[#E0A83E]/12 border border-[#E0A83E]/40 rounded-2xl px-4 py-2.5 flex items-center gap-3">
        <MailCheck size={16} className="text-[#7A4E0B] shrink-0" />
        <p className="text-xs text-[#222]/75 min-w-0 flex-1">
          Please verify your email to send messages, post, or leave a review.
        </p>
        <button onClick={resend} disabled={sending}
          className="shrink-0 text-xs font-semibold text-[#7A4E0B] underline underline-offset-2 disabled:opacity-50"
          data-testid="verify-email-banner-resend">
          {sending ? "Sending…" : "Resend email"}
        </button>
        <button onClick={dismiss} aria-label="Dismiss"
          className="p-1 rounded-lg hover:bg-black/5 shrink-0"
          data-testid="verify-email-banner-dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
